import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MultiSelect } from '@/components/ui/multi-select';
import axiosInstance from '@/lib/axios';
import { useUser } from '@/hooks/use-user';
import { cn } from '@/lib/utils';
import {
  COMMUNITY_PERMISSION_LABELS,
  CONFIGURABLE_COMMUNITY_PERMISSIONS,
  MATRIX_COMMUNITY_ROLE_LABELS,
  MATRIX_COMMUNITY_ROLES,
  type ConfigurableCommunityPermission,
  type MatrixCommunityRole,
} from '@/lib/community-permissions';

type ExtraUser = { id: string; name: string };

type PolicyPermission = {
  key: ConfigurableCommunityPermission;
  roles: MatrixCommunityRole[];
  extraUsers: ExtraUser[];
};

type PolicyRow = {
  roles: MatrixCommunityRole[];
  userIds: string[];
};

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

function emptyRows(): Record<ConfigurableCommunityPermission, PolicyRow> {
  return {
    create_subcommunity: { roles: [], userIds: [] },
    create_general_post: { roles: [], userIds: [] },
    manage_new_members: { roles: [], userIds: [] },
    view_active_user_status: { roles: [], userIds: [] },
  };
}

export function RoleAccessSettings({ communityId }: { communityId: string }) {
  const { mutate: mutateUser } = useUser();
  const { data, isLoading, mutate } = useSWR<{ permissions: PolicyPermission[] }>(
    `/api/communities/${communityId}/access-policy`,
    fetcher
  );

  const [rows, setRows] = useState<Record<ConfigurableCommunityPermission, PolicyRow>>(emptyRows);
  const [memberOptions, setMemberOptions] = useState<{ label: string; value: string }[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadMembers = async () => {
      try {
        const res = await axiosInstance.get(`/api/communities/${communityId}/members`, {
          params: { limit: 50 },
        });
        if (cancelled) return;
        const found = ((res.data.members ?? []) as ExtraUser[]).map((member) => ({
          value: member.id,
          label: member.name,
        }));
        setMemberOptions((current) => mergeOptions(current, found));
      } catch {
        // keep existing options
      }
    };
    void loadMembers();
    return () => {
      cancelled = true;
    };
  }, [communityId]);

  useEffect(() => {
    if (!data?.permissions) return;
    const next = emptyRows();
    const extras: { label: string; value: string }[] = [];
    for (const permission of data.permissions) {
      next[permission.key] = {
        roles: permission.roles,
        userIds: permission.extraUsers.map((user) => user.id),
      };
      for (const user of permission.extraUsers) {
        extras.push({ value: user.id, label: user.name });
      }
    }
    setRows(next);
    setMemberOptions((current) => mergeOptions(current, extras));
  }, [data]);

  const handleSearch = async (value: string) => {
    if (value.trim().length < 2) return;
    try {
      const res = await axiosInstance.get(`/api/communities/${communityId}/members`, {
        params: { search: value.trim(), limit: 50 },
      });
      const found = ((res.data.members ?? []) as ExtraUser[]).map((member) => ({
        value: member.id,
        label: member.name,
      }));
      setMemberOptions((current) => mergeOptions(current, found));
    } catch {
      // keep existing options
    }
  };

  const toggleRole = (permission: ConfigurableCommunityPermission, role: MatrixCommunityRole) => {
    setRows((current) => {
      const selected = current[permission].roles;
      const roles = selected.includes(role)
        ? selected.filter((item) => item !== role)
        : [...selected, role];
      return { ...current, [permission]: { ...current[permission], roles } };
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axiosInstance.put(`/api/communities/${communityId}/access-policy`, {
        grants: CONFIGURABLE_COMMUNITY_PERMISSIONS.map((permission) => ({
          permission,
          roles: rows[permission].roles,
          userIds: rows[permission].userIds,
        })),
      });
      toast.success('Permissions updated');
      await mutate();
      await mutateUser();
    } catch {
      toast.error('Failed to update permissions');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Roles & Permissions</CardTitle>
        <CardDescription>
          Choose which roles can take each action. Admins always have every permission. You can also
          grant an action to specific people without changing their role.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 overflow-visible">
        <div className="w-full min-w-0">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-border/60 text-left">
                <th className="py-2 pr-4 font-medium">Action</th>
                {MATRIX_COMMUNITY_ROLES.map((role) => (
                  <th key={role} className="px-2 py-2 font-medium whitespace-nowrap">
                    {MATRIX_COMMUNITY_ROLE_LABELS[role]}
                  </th>
                ))}
                <th className="px-2 py-2 font-medium">Admin</th>
                <th className="py-2 pl-4 font-medium min-w-[220px]">Extra people</th>
              </tr>
            </thead>
            <tbody>
              {CONFIGURABLE_COMMUNITY_PERMISSIONS.map((permission, index) => {
                const isLast = index === CONFIGURABLE_COMMUNITY_PERMISSIONS.length - 1;
                return (
                  <tr
                    key={permission}
                    className={cn('border-b border-border/40 align-top', isLast && 'relative z-20')}
                  >
                    <td className="py-3 pr-4 font-medium">
                      {COMMUNITY_PERMISSION_LABELS[permission]}
                    </td>
                    {MATRIX_COMMUNITY_ROLES.map((role) => (
                      <td key={role} className="px-2 py-3">
                        <Checkbox
                          checked={rows[permission].roles.includes(role)}
                          onCheckedChange={() => toggleRole(permission, role)}
                          aria-label={`${COMMUNITY_PERMISSION_LABELS[permission]} for ${role}`}
                        />
                      </td>
                    ))}
                    <td className="px-2 py-3">
                      <Checkbox checked disabled />
                    </td>
                    <td className={cn('py-3 pl-4', isLast && 'pb-28')}>
                      <MultiSelect
                        placeholder="Add members…"
                        options={memberOptions}
                        selected={rows[permission].userIds}
                        creatable={false}
                        showScrollArrows
                        onSearch={handleSearch}
                        onChange={(userIds) =>
                          setRows((current) => ({
                            ...current,
                            [permission]: { ...current[permission], userIds },
                          }))
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end relative z-0">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save permissions
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function mergeOptions(
  current: { label: string; value: string }[],
  incoming: { label: string; value: string }[]
) {
  const byValue = new Map(current.map((option) => [option.value, option]));
  for (const option of incoming) {
    byValue.set(option.value, option);
  }
  return [...byValue.values()];
}
